import type {
  ActionQueue,
  ActionQueueRequest,
  ActionQueueResult,
  ActionQueueStats,
  AppUpdater,
  KioskState,
  PendingAction,
  PlatformBridge,
  OpenFileDialogOptions,
  SecureStore,
  UpdateStatusSnapshot,
} from './PlatformBridge.js';

/**
 * Desktop implementation of PlatformBridge, used when the React app runs
 * inside the Electron renderer. It is a thin wrapper around the contextBridge
 * surface exposed by `packages/desktop/src/preload/index.ts`.
 *
 * Step 6 of Forge_iDAS_Desktop_Developer_Instructions.docx:
 *   - 6.1 Silent / direct printing  → printSilent → desktop.printPdf
 *   - 6.2 Native open/save dialogs  → saveFile / openFileDialog
 *   - 6.3 OS-secure token storage   → secureStore (safeStorage-backed)
 *
 * No React component should ever import this module directly — the frontend
 * picks one of the bridges in `frontend/src/services/platformBridge.ts`,
 * and components consume the singleton from there.
 */

interface DesktopFilePayload {
  name: string;
  type: string;
  lastModified: number;
  bytes: Uint8Array;
}

interface DesktopSaveResult {
  saved: boolean;
  filePath?: string;
}

interface DesktopPrinterInfo {
  name: string;
  displayName: string;
  isDefault: boolean;
  status: number;
}

/**
 * Shape of the `window.desktop` object exposed by the preload script. We
 * redeclare it here (rather than importing from `@forgedas/desktop`) so the
 * shared-core package does not depend on Electron's runtime types.
 */
export interface DesktopBridgeApi {
  appVersion(): string;
  isDesktop: true;
  printPdf(req: {
    pdf: Uint8Array;
    printerName?: string;
    copies?: number;
  }): Promise<void>;
  listPrinters(): Promise<DesktopPrinterInfo[]>;
  saveFile(req: { suggestedName: string; bytes: Uint8Array }): Promise<DesktopSaveResult>;
  openFileDialog(req?: {
    title?: string;
    multi?: boolean;
    filters?: Array<{ name: string; extensions: string[] }>;
  }): Promise<DesktopFilePayload[]>;
  secureStore: {
    get(key: string): Promise<string | null>;
    set(key: string, value: string): Promise<void>;
    delete(key: string): Promise<void>;
  };
  queue: {
    enqueue(req: ActionQueueRequest): Promise<ActionQueueResult>;
    list(): Promise<PendingAction[]>;
    stats(): Promise<ActionQueueStats>;
    /** T84 — drain now because connectivity returned. */
    flush(): Promise<void>;
    retry(id: string): Promise<void>;
    discard(id: string): Promise<void>;
    onChange(cb: (stats: ActionQueueStats) => void): () => void;
  };
  updates: {
    getStatus(): Promise<UpdateStatusSnapshot>;
    onStatus(cb: (status: UpdateStatusSnapshot) => void): () => void;
    quitAndInstall(): Promise<void>;
    checkForUpdates(): Promise<void>;
  };
  kiosk: KioskState;
  onUpdateAvailable(cb: () => void): () => void;
}

function payloadToFile(p: DesktopFilePayload): File {
  // Copy into a fresh Uint8Array so the Blob constructor accepts it across
  // every TS lib target without ArrayBufferLike-union complaints.
  const copy = new Uint8Array(p.bytes);
  return new File([copy.buffer], p.name, {
    type: p.type,
    lastModified: p.lastModified,
  });
}

export interface DesktopPlatformBridgeOptions {
  /** Override the runtime bridge — useful for unit tests. */
  bridge?: DesktopBridgeApi;
}

/**
 * Build a PlatformBridge backed by `window.desktop`. Throws if the desktop
 * surface is missing — callers should gate on `window.desktop?.isDesktop`
 * (or use the chooser in `frontend/src/services/platformBridge.ts`).
 */
export function createDesktopPlatformBridge(
  opts: DesktopPlatformBridgeOptions = {},
): PlatformBridge {
  const desktop =
    opts.bridge ?? ((globalThis as unknown as { desktop?: DesktopBridgeApi }).desktop ?? undefined);

  if (!desktop || desktop.isDesktop !== true) {
    throw new Error(
      'createDesktopPlatformBridge: window.desktop is not available. ' +
        'This function may only be called from the Electron renderer.',
    );
  }

  const secureStore: SecureStore = {
    get: (key) => desktop.secureStore.get(key),
    set: (key, value) => desktop.secureStore.set(key, value),
    delete: (key) => desktop.secureStore.delete(key),
  };

  const actionQueue: ActionQueue = {
    enqueue: (req) => desktop.queue.enqueue(req),
    list: () => desktop.queue.list(),
    stats: () => desktop.queue.stats(),
    flush: () => desktop.queue.flush(),
    retry: (id) => desktop.queue.retry(id),
    discard: (id) => desktop.queue.discard(id),
    onChange: (cb) => desktop.queue.onChange(cb),
  };

  const updates: AppUpdater = {
    enabled: true,
    getStatus: () => desktop.updates.getStatus(),
    onStatus: (cb) => desktop.updates.onStatus(cb),
    quitAndInstall: () => desktop.updates.quitAndInstall(),
    checkForUpdates: () => desktop.updates.checkForUpdates(),
  };

  return {
    isDesktop: true,
    appVersion: () => desktop.appVersion(),
    onUpdateAvailable: (cb) => {
      // The preload returns an unsubscribe; the PlatformBridge contract
      // returns void, so we drop the unsubscribe. (M5 may want to refine
      // the interface to surface the unsubscribe to callers.)
      desktop.onUpdateAvailable(cb);
    },
    secureStore,
    actionQueue,
    updates,
    kiosk: desktop.kiosk,

    async printSilent(pdf: Uint8Array, printerName?: string) {
      await desktop.printPdf({ pdf, printerName });
    },

    async saveFile(name: string, bytes: Uint8Array): Promise<string> {
      const result = await desktop.saveFile({ suggestedName: name, bytes });
      return result.saved && result.filePath ? result.filePath : '';
    },

    async openFileDialog(opts: OpenFileDialogOptions = {}): Promise<File[]> {
      const payloads = await desktop.openFileDialog({
        title: opts.title,
        multi: opts.multi,
        filters: opts.filters,
      });
      return payloads.map(payloadToFile);
    },
  };
}

/**
 * Convenience helper: returns the desktop bridge if running inside Electron,
 * otherwise null. The frontend bootstrap uses this to decide which
 * PlatformBridge implementation to install.
 */
export function getDesktopBridgeApi(): DesktopBridgeApi | null {
  const candidate = (globalThis as unknown as { desktop?: DesktopBridgeApi }).desktop;
  if (candidate && candidate.isDesktop === true) return candidate;
  return null;
}
